import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDownIcon, CheckIcon } from './Icons';

const MultiSelect = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder = "Select options...", 
  className,
  maxDisplay = 2 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownRect, setDropdownRect] = useState(null);
  const triggerRef = useRef(null);
  const portalRef = useRef(null);
  const { theme } = useTheme();

  const updatePosition = () => {
    if (triggerRef.current && isOpen) {
      setDropdownRect(triggerRef.current.getBoundingClientRect());
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = triggerRef.current?.contains(event.target);
      const inPortal = portalRef.current?.contains(event.target);
      if (!inTrigger && !inPortal) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Normalize options to handle both simple values and {value, label} objects
  const normalizedOptions = options.map(opt => {
    if (typeof opt === 'object' && opt !== null && 'value' in opt) {
      return { value: opt.value, label: opt.label || String(opt.value) };
    }
    return { value: opt, label: String(opt) };
  });

  // Create a map for quick label lookup
  const labelMap = new Map();
  normalizedOptions.forEach(opt => {
    labelMap.set(String(opt.value), opt.label);
    labelMap.set(Number(opt.value), opt.label);
  });

  const filteredOptions = normalizedOptions.filter(option => {
    const searchLower = searchTerm.toLowerCase();
    return option.label.toLowerCase().includes(searchLower) || 
           String(option.value).toLowerCase().includes(searchLower);
  });

  const handleToggleOption = (optionValue) => {
    const stringValue = String(optionValue);
    const newValue = value.some(v => String(v) === stringValue || Number(v) === Number(optionValue))
      ? value.filter(v => String(v) !== stringValue && Number(v) !== Number(optionValue))
      : [...value, optionValue];
    onChange(newValue);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const displayText = () => {
    if (value.length === 0) return placeholder;
    const labels = value.map(v => labelMap.get(v) || labelMap.get(String(v)) || String(v));
    if (value.length <= maxDisplay) return labels.join(', ');
    return `${labels.slice(0, maxDisplay).join(', ')} +${value.length - maxDisplay}`;
  };

  const openDropdown = () => {
    if (triggerRef.current) {
      setDropdownRect(triggerRef.current.getBoundingClientRect());
    }
    setIsOpen(true);
  };

  const dropdownContent = isOpen && dropdownRect && typeof document !== 'undefined' && (
    <div
      ref={portalRef}
      className={cn(
        "fixed z-[9999] border rounded-md shadow-lg max-h-60 overflow-hidden",
        theme.colors.surface,
        theme.border.light
      )}
      style={{
        top: dropdownRect.bottom + 4,
        left: dropdownRect.left,
        width: Math.max(dropdownRect.width, 200),
      }}
    >
      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
        <input
          type="text"
          placeholder="Search options..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={cn(
            "w-full px-2 py-1 text-xs border rounded",
            theme.colors.surface,
            theme.text.primary,
            theme.border.light,
            "focus:outline-none focus:ring-1 focus:ring-blue-500"
          )}
        />
        {value.length > 0 && (
          <button
            type="button"
            onClick={handleClearAll}
            className="mt-1 text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            Clear All ({value.length})
          </button>
        )}
      </div>
      <div className="max-h-40 overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className={cn("px-3 py-2 text-sm", theme.text.muted)}>
            No options found
          </div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = value.some(v =>
              String(v) === String(option.value) || Number(v) === Number(option.value)
            );
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleToggleOption(option.value)}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2",
                  isSelected ? "bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300" : theme.text.primary
                )}
              >
                <div className={cn(
                  "w-4 h-4 border-2 rounded flex items-center justify-center",
                  isSelected
                    ? "bg-blue-500 border-blue-500"
                    : "border-gray-300 dark:border-gray-600"
                )}>
                  {isSelected && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="flex-1">{option.label}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div className="relative" ref={triggerRef}>
      <button
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : openDropdown())}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          theme.colors.surface,
          theme.text.primary,
          theme.border.light,
          value.length > 0 ? "border-blue-300 bg-blue-50 dark:bg-blue-950/20" : "",
          className
        )}
      >
        <span className={cn(
          "truncate flex-1 text-left",
          value.length === 0 ? theme.text.muted : theme.text.primary
        )}>
          {displayText()}
        </span>
        <div className="flex items-center gap-1">
          {value.length > 0 && (
            <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
              {value.length}
            </span>
          )}
          <ChevronDownIcon className={cn("w-4 h-4 transition-transform", isOpen ? "rotate-180" : "")} />
        </div>
      </button>

      {typeof document !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export { MultiSelect };
